// Document Processing Service - Handles multiple file types
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

// Set up PDF.js worker - use a local worker path
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

class DocumentProcessor {
  async processDocument(file) {
    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();

    try {
      let text = '';

      if (fileType === 'text/plain' || fileName.endsWith('.txt')) {
        text = await this.processTextFile(file);
      } else if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
        text = await this.processPdfFile(file);
      } else if (fileType.includes('word') || fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
        text = await this.processWordFile(file);
      } else if (fileType.includes('excel') || fileType.includes('spreadsheet') || 
                 fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        text = await this.processExcelFile(file);
      } else if (fileType.includes('powerpoint') || fileType.includes('presentation') || 
                 fileName.endsWith('.pptx') || fileName.endsWith('.ppt')) {
        text = await this.processPowerPointFile(file);
      } else if (fileType === 'text/csv' || fileName.endsWith('.csv')) {
        text = await this.processCsvFile(file);
      } else {
        throw new Error(`Unsupported file type: ${fileType}`);
      }

      return {
        success: true,
        text: this.validateText(text),
        fileName: file.name,
        fileType: fileType,
        metadata: {
          fileName: file.name,
          fileType: fileType,
          fileSize: file.size,
          extractedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('❌ Error processing document:', error);
      console.error('❌ Document processing failed for:', file.name);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      return {
        success: false,
        error: error.message || 'Unknown error occurred',
        fileName: file.name,
        fileType: fileType,
        metadata: null
      };
    }
  }

  async processTextFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  async processPdfFile(file) {
    try {
      console.log('📄 Starting PDF processing for:', file.name);
      const arrayBuffer = await file.arrayBuffer();
      console.log('📄 Array buffer created, size:', arrayBuffer.byteLength);
      
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      console.log('📄 PDF loaded, pages:', pdf.numPages);
      
      let text = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        console.log(`📄 Processing page ${i}/${pdf.numPages}`);
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        text += pageText + '\n';
      }

      console.log('📄 PDF processing completed, text length:', text.length);
      return text;
    } catch (error) {
      console.error('❌ Error processing PDF:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw new Error(`Failed to process PDF file: ${error.message}`);
    }
  }

  async processWordFile(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      console.error('Error processing Word document:', error);
      throw new Error('Failed to process Word document');
    }
  }

  async processExcelFile(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      let text = '';

      workbook.SheetNames.forEach(sheetName => {
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        text += `Sheet: ${sheetName}\n`;
        jsonData.forEach(row => {
          text += row.join('\t') + '\n';
        });
        text += '\n';
      });

      return text;
    } catch (error) {
      console.error('Error processing Excel file:', error);
      throw new Error('Failed to process Excel file');
    }
  }

  async processPowerPointFile(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      let text = '';

      workbook.SheetNames.forEach(sheetName => {
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        text += `Slide: ${sheetName}\n`;
        jsonData.forEach(row => {
          text += row.join('\t') + '\n';
        });
        text += '\n';
      });

      return text;
    } catch (error) {
      console.error('Error processing PowerPoint file:', error);
      throw new Error('Failed to process PowerPoint file');
    }
  }

  async processCsvFile(file) {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        complete: (results) => {
          if (results.errors.length > 0) {
            reject(new Error('Failed to parse CSV file'));
          } else {
            const text = results.data.map(row => row.join(',')).join('\n');
            resolve(text);
          }
        },
        error: (error) => {
          reject(new Error('Failed to parse CSV file'));
        }
      });
    });
  }

  validateText(text) {
    if (!text || typeof text !== 'string') {
      return '';
    }

    // Remove null characters and other control characters
    // eslint-disable-next-line no-control-regex
    return text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
               .replace(/\s+/g, ' ')
               .trim();
  }

  isSupported(fileType) {
    const supportedTypes = [
      'text/plain',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/csv'
    ];
    
    return supportedTypes.includes(fileType);
  }
}

export const documentProcessor = new DocumentProcessor();
export default documentProcessor;

